import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import { t } from "i18next";
import ItemDto from "../../../models/ItemDto";
import DialogActionsButtons from "../DialogActionsButton";
import DialogTransition from "../DialogTransition";
import Options from "../../menuCard/options/Options";
import ItemExtras, { ItemExtraForm } from "../../menuCard/extras/ItemExtras";
import { FormProvider, useForm } from "react-hook-form";
import ItemOptionPriceCalculator from "./ItemOptionPriceCalculator";
import { useCallback, useMemo, useRef } from "react";
import ItemOptionGroupDto from "../../../models/ItemOptionGroupDto";
import CartItemExtra from "../../../models/CartItemExtra";
import CardItem from "../../../models/Carditem";

export interface SelectedItemOptions {
    // this contains only the selected groups
    itemOptionGroups: ItemOptionGroupDto[];
    itemExtras: CartItemExtra[];
}

interface ItemOptionsDialogProps {
    item: ItemDto | CardItem;
    close?: () => void
    onConfirmation?: (selectedOptions: SelectedItemOptions) => void;
    closeOnConfirm?: boolean;
    opened: boolean;
}

export default function ItemOptionsDialog({ item, opened, close, closeOnConfirm = true, onConfirmation }: ItemOptionsDialogProps) {
    const formMethods = useForm();
    const { getValues } = formMethods;
    const ref = useRef<HTMLDivElement>(null);

    const isCardItem = useMemo(() => {
        return item instanceof CardItem;
    }, [item]);

    const getSelectedOptions = useCallback((): SelectedItemOptions => {
        const itemExtras: ItemExtraForm[] = getValues('itemExtras');
        const itemExtrasFromForm: CartItemExtra[] = Array.from(Object.keys(itemExtras)).map(itemExtraId => {
            const itemExtra = item.itemExtras.filter(extra => extra.id === Number.parseInt(itemExtraId))[0];
            const amount = Number.parseInt(getValues('itemExtras.' + itemExtraId + '.amount'));
            return { ...itemExtra, amount: amount }
        });
        const itemOptionGroups: number[] = getValues('options');
        const itemOptionGroupsFromForm: ItemOptionGroupDto[] = Array.from(Object.keys(itemOptionGroups)).map(optionGroupId => {
            const optionGroup = item.itemOptionGroups.filter(optionGroup => Number.parseInt(optionGroupId) === optionGroup.id)[0];
            const selectedOptionIdFromGroup = getValues('options.' + optionGroupId);
            if (!optionGroup || !selectedOptionIdFromGroup) throw new Error(`Can't find matching option`);
            const selectedOption = optionGroup.itemOptions.filter(option => option.id === Number.parseInt(selectedOptionIdFromGroup))[0];
            return { ...optionGroup, itemOptions: [selectedOption] };
        })
        return { itemExtras: itemExtrasFromForm, itemOptionGroups: itemOptionGroupsFromForm };
    }, [getValues, item.itemExtras, item.itemOptionGroups]);

    return (
        <>
            {opened &&
                <Dialog
                    open={opened}
                    TransitionComponent={DialogTransition}
                    keepMounted
                    onClose={close}
                    aria-describedby="alert-dialog-slide-description"
                >
                    <DialogTitle>
                        {`${item.value.value}`}
                    </DialogTitle>
                    <DialogContent ref={ref}>
                        <FormProvider {...formMethods}>
                            <Options optionGroups={item.itemOptionGroups} selectedOptions={isCardItem ? (item as CardItem).selectedOptions : undefined} />
                            <ItemExtras itemExtras={item.itemExtras} selectedOptions={isCardItem ? (item as CardItem).selectedOptions : undefined}/>
                            <ItemOptionPriceCalculator
                                itemPrice={item.price}
                                optionGroups={item.itemOptionGroups}
                                itemExtras={item.itemExtras} />
                        </FormProvider>
                        <DialogActionsButtons
                            parentRef={ref}
                            floating
                            onClick={() => {
                                if (onConfirmation) {
                                    onConfirmation(getSelectedOptions());
                                }
                                if (closeOnConfirm && close) {
                                    close();
                                }
                            }}
                            buttonText={t("confirm")}
                            onClickCancel={close} />
                    </DialogContent>
                </Dialog>
            }
        </>
    );
}